import { useApolloClient, useQuery } from "@apollo/client";
import { ArrowUpIcon, PaperClipIcon, XIcon } from "@heroicons/react/outline";
import { Editor } from "@tiptap/core";
import { truncate } from "lodash";
import {
  ChangeEvent,
  ClipboardEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import {
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import ChatHistoryPopover from "@apps/home/components/chat-history-popover";
import { currentOrganizationVar, errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import AppPopover from "@components/popover/app-popover";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import TextOnlyWysiwyg from "@components/wysiwyg/text-only-wysiwyg";
import { classNames } from "@helpers/css";

import { ChatSessionContext } from "../ask-ai-context";
import {
  AskAIAttachment,
  attachmentAccept,
  getAttachmentFormat,
  maxAskAIAttachments,
  uploadAskAIFile,
} from "../attachments";
import getAskAiSessionQuery from "../graphql/get-ask-ai-session-query";
import searchSkillsQuery from "../graphql/search-skills-query";
import { getChatSessionTitle } from "../helpers";
import SkillCommands, { SkillItem } from "../wysiwyg/skill-commands-extension";
import SkillMention from "../wysiwyg/skill-mention-node";

const AskAIForm = forwardRef(
  (
    {
      chatSessionId,
      inputMessage,
      loadingCreateMessage,
      loadingRetryAskAiMessage,
      attachments,
      autoFocus = false,
      onSubmit,
      onEditorReady,
      onChangeInputValue,
      onChangeAttachments,
      onClickChatSessionId,
      showChatHistory = false,
      isIncognito = false,
      showStopButton = false,
      onStopResponse,
      collapsibleInput = false,
    }: {
      chatSessionId: number | null;
      inputMessage: string;
      attachments: AskAIAttachment[];
      autoFocus: boolean;
      loadingCreateMessage: boolean;
      loadingRetryAskAiMessage: boolean;
      onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
      onEditorReady: (editor: Editor) => void;
      onChangeInputValue: (value: string) => void;
      onChangeAttachments: Dispatch<SetStateAction<AskAIAttachment[]>>;
      onClickChatSessionId?: (chatSessionId: number | null) => void;
      showChatHistory?: boolean;
      isIncognito?: boolean;
      /** While a response is in flight, the send button becomes a stop button
       * (TF-1512) that calls onStopResponse instead of submitting. */
      showStopButton?: boolean;
      onStopResponse?: () => void;
      /** Start as a single-line field and grow to the full form (toolbar and
       * all) once focused, or as soon as there is something to send. Used on the
       * homepage (TF-1590) so the input does not dominate the page. */
      collapsibleInput?: boolean;
    },
    ref: React.Ref<HTMLFormElement>,
  ) => {
    const { meetingId, assessmentId } = useContext(ChatSessionContext);
    const history = useHistory();
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Local handle on the editor so the `+` menu can insert a skill chip
    // directly (the `/` picker inserts via its own suggestion command).
    const formEditorRef = useRef<Editor | null>(null);
    const [skillItems, setSkillItems] = useState<SkillItem[]>([]);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const hasSubmittableContent =
      inputMessage.trim().length > 0 ||
      attachments.some((attachment) => !!attachment.url);
    // Collapsed = one line, no toolbar. Anything the user has started (typed
    // text, a file still uploading) or the Temporary Chat callout keeps the
    // form open, so nothing they can act on is ever hidden.
    const isCollapsed =
      collapsibleInput &&
      !isInputFocused &&
      !hasSubmittableContent &&
      attachments.length === 0 &&
      !isIncognito;

    const handleFocusForm = () => setIsInputFocused(true);
    const handleBlurForm = (event: React.FocusEvent<HTMLFormElement>) => {
      // Focus moving to a control inside the form (e.g. the `+` menu) must not
      // collapse it from under the user's click.
      if (event.currentTarget.contains(event.relatedTarget)) return;
      setIsInputFocused(false);
    };
    const isUploadingFiles = attachments.some(
      (attachment) => attachment.isUploading,
    );
    const buttonClassName = classNames(
      "h-6 w-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500",
      hasSubmittableContent && "bg-blue-link text-white hover:bg-blue-800",
    );
    const currentOrganization = currentOrganizationVar();

    const apollo = useApolloClient();
    const organizationId = currentOrganization?.id ?? null;

    // Keep a synchronously-readable snapshot of the skill list so the `/` picker
    // can render instantly, plus the in-flight fetch promise (deduped per org)
    // so the first open before the snapshot lands can await it.
    const skillsSnapshotRef = useRef<{
      orgId: number | null;
      items: SkillItem[];
    } | null>(null);
    const skillsPromiseRef = useRef<{
      orgId: number | null;
      promise: Promise<SkillItem[]>;
    } | null>(null);

    const loadAllSkills = useCallback(
      (force = false): Promise<SkillItem[]> => {
        const cached = skillsPromiseRef.current;
        if (!force && cached && cached.orgId === organizationId) {
          return cached.promise;
        }
        const promise = apollo
          .query({
            query: searchSkillsQuery,
            variables: { organizationId },
            fetchPolicy: "no-cache",
          })
          .then((res) =>
            (
              (res.data?.searchSkills ?? []) as Array<
                Omit<SkillItem, "id"> & { id?: string; platformId?: string }
              >
            ).map(
              (raw): SkillItem => ({
                ...raw,
                // Platform skills come back under the `platformId` alias (their
                // id is a String, not a UUID); everything else uses `id`.
                id: raw.id ?? raw.platformId ?? "",
              }),
            ),
          )
          .catch(() => [] as SkillItem[]);
        skillsPromiseRef.current = { orgId: organizationId, promise };
        // Update the synchronous snapshot once the fetch resolves so later
        // opens are instant and reflect skills created during this chat, and
        // mirror it into state so the `+` dropdown re-renders with the skills.
        promise.then((items) => {
          skillsSnapshotRef.current = { orgId: organizationId, items };
          setSkillItems(items);
        });
        return promise;
      },
      [apollo, organizationId],
    );

    const fetchSkillItems = useCallback(
      async ({ query }: { query: string }): Promise<SkillItem[]> => {
        const q = query.trim().toLowerCase();
        // When the picker first opens (`/`), refresh in the background so newly
        // created skills appear next time — without blocking on the network.
        if (!q) void loadAllSkills(true);
        const snapshot = skillsSnapshotRef.current;
        const items =
          snapshot && snapshot.orgId === organizationId
            ? snapshot.items
            : await loadAllSkills();
        if (!q) return items;
        return items.filter((item) =>
          [item.name, item.slug, item.description].some((field) =>
            (field ?? "").toLowerCase().includes(q),
          ),
        );
      },
      [loadAllSkills, organizationId],
    );

    // Warm the skill cache as soon as the form renders so the `/` picker and the
    // `+` menu's skill list feel instant the first time they're opened.
    useEffect(() => {
      void loadAllSkills();
    }, [loadAllSkills]);

    const extraExtensions = useMemo(() => {
      return [
        SkillCommands.configure({
          items: fetchSkillItems,
        }),
        SkillMention,
      ];
    }, [fetchSkillItems]);

    // Insert a skill chip at the cursor (used by the `+` menu rows). Mirrors
    // the node the `/` picker inserts, followed by a trailing space.
    const insertSkillChip = (skill: SkillItem) => {
      formEditorRef.current
        ?.chain()
        .focus()
        .insertContent([
          {
            type: "skillMention",
            attrs: {
              slug: skill.slug,
              skillId: skill.id,
              scope: skill.visibility,
            },
          },
          { type: "text", text: " " },
        ])
        .run();
    };

    useEffect(() => {
      if (window.Intercom) {
        window.Intercom("update", { hide_default_launcher: true });
      }
      return function cleanup() {
        if (window.Intercom) {
          window.Intercom("update", { hide_default_launcher: false });
        }
      };
    }, []);

    const handleRemoveAttachment = (attachmentId: string) => {
      onChangeAttachments((attachments) => {
        const attachment = attachments.find(({ id }) => id === attachmentId);
        if (attachment) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
        return attachments.filter(({ id }) => id !== attachmentId);
      });
    };

    const handleClickAddFile = () => {
      fileInputRef.current?.click();
    };

    const uploadFiles = (files: File[]) => {
      const supportedFiles = files.filter(
        (file) => !!getAttachmentFormat(file),
      );
      if (supportedFiles.length !== files.length) {
        errorNotificationVar({
          title: "Unsupported file format.",
          description:
            "Use PDF, DOCX, TXT, Markdown, CSV, PNG, JPEG, WEBP, or GIF.",
        });
      }
      const uploadableFiles = supportedFiles.filter(
        (file) => file.size <= getAttachmentFormat(file)!.maxBytes,
      );
      if (uploadableFiles.length !== supportedFiles.length) {
        errorNotificationVar({
          title:
            "Documents must be 4 MB or smaller; images must be 5 MB or smaller.",
        });
      }
      const availableSlots = maxAskAIAttachments - attachments.length;
      if (availableSlots <= 0) {
        errorNotificationVar({
          title: `You can upload up to ${maxAskAIAttachments} files.`,
        });
        return;
      }
      const filesToUpload = uploadableFiles.slice(0, availableSlots);
      if (filesToUpload.length < uploadableFiles.length) {
        errorNotificationVar({
          title: `You can upload up to ${maxAskAIAttachments} files.`,
        });
      }
      const newAttachments = filesToUpload.map((file) => ({
        id: uuidv4(),
        filename: file.name,
        isImage: getAttachmentFormat(file)!.isImage,
        previewUrl: URL.createObjectURL(file),
        isUploading: true,
      }));
      onChangeAttachments((attachments) => [...attachments, ...newAttachments]);
      filesToUpload.forEach((file, index) => {
        const attachmentId = newAttachments[index].id;
        uploadAskAIFile(file, currentOrganization.id)
          .then((url) => {
            if (!url) {
              throw new Error("The file could not be uploaded.");
            }
            onChangeAttachments((attachments) =>
              attachments.map((attachment) =>
                attachment.id === attachmentId
                  ? { ...attachment, url, isUploading: false }
                  : attachment,
              ),
            );
          })
          .catch(() => {
            errorNotificationVar({
              title: "The file could not be uploaded. Please try again.",
            });
            handleRemoveAttachment(attachmentId);
          });
      });
    };

    const handleChangeFileInput = (e: ChangeEvent<HTMLInputElement>) => {
      uploadFiles(Array.from(e.target.files || []));
      e.target.value = "";
    };

    const handlePasteFiles = (e: ClipboardEvent<HTMLDivElement>) => {
      const files = Array.from(e.clipboardData.files || []);
      if (!files.length) {
        return;
      }
      e.preventDefault();
      uploadFiles(files);
    };

    // Reactive cache-only read instead of a synchronous cache.readQuery on every
    // keystroke: Apollo only recomputes when this session's cache entry actually
    // changes, not on every input re-render (TF-1577).
    const { data: cachedSessionData } = useQuery<
      GetAskAiChatSessionQueryQuery,
      GetAskAiChatSessionQueryQueryVariables
    >(getAskAiSessionQuery, {
      variables: { chatSessionId: chatSessionId || 0 },
      fetchPolicy: "cache-only",
      skip: !chatSessionId,
    });
    const cachedChatSession = cachedSessionData?.chatSession;
    const chatSessionTitle = truncate(getChatSessionTitle(cachedChatSession), {
      length: 30,
    });

    return (
      <form
        className={classNames(
          "border rounded-xl bg-white",
          // Collapsed the field is a narrow pill; focusing it widens it back to
          // the container. max-width animates, so the growth is one motion with
          // the height change below.
          collapsibleInput &&
            "mx-auto transition-[max-width] duration-200 ease-out",
          // 672px inside the 900px homepage container: clearly narrower, and
          // still wide enough that the starter-prompt chips below line up.
          collapsibleInput && (isCollapsed ? "max-w-2xl" : "max-w-full"),
        )}
        onSubmit={onSubmit}
        onFocus={collapsibleInput ? handleFocusForm : undefined}
        onBlur={collapsibleInput ? handleBlurForm : undefined}
        ref={ref}
      >
        {isIncognito && (
          <Callout
            className="m-3 mb-0"
            icon="lockClosed"
            size="small"
            title="Temporary Chat"
          >
            <div className="text-sm">
              This conversation won't be saved to your history or remembered by
              Topicflow AI.
            </div>
          </Callout>
        )}
        <div className="relative" onPaste={handlePasteFiles}>
          <TextOnlyWysiwyg
            key={chatSessionId}
            value={inputMessage}
            onChangeValue={onChangeInputValue}
            placeholder={
              meetingId
                ? "Ask anything about this meeting..."
                : assessmentId
                  ? `Message Topicflow AI`
                  : "Ask anything..."
            }
            className={classNames(
              "js-ask-ai-input",
              "w-full border-none focus:ring-0 focus:outline-hidden",
              "transition-[padding,min-height] duration-200 ease-out",
              // Collapsed: even padding above and below, so the single line sits
              // centred whatever the line height. Expanded: the taller box with
              // room on the right for the send button.
              isCollapsed
                ? "px-4 py-2.5"
                : "p-4 pr-16 pb-1 min-h-16 max-h-48 overflow-y-auto",
            )}
            editable
            autofocus={autoFocus}
            extraExtensions={extraExtensions}
            onEnterPressed={onSubmit}
            onEditorReady={(ed) => {
              formEditorRef.current = ed;
              onEditorReady(ed);
            }}
          />
        </div>
        {attachments.length > 0 && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className={classNames(
                  "group/image relative h-16 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50",
                  attachment.isImage ? "w-16" : "w-40",
                )}
              >
                <a
                  href={attachment.url || attachment.previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block h-full w-full"
                >
                  {attachment.isImage ? (
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.filename}
                      className={classNames(
                        "h-full w-full object-cover",
                        attachment.isUploading && "opacity-60",
                      )}
                    />
                  ) : (
                    <span className="flex h-full flex-col items-center justify-center gap-1 p-1 text-center text-xs">
                      <TopicflowIcon
                        type="file04"
                        className="size-5 shrink-0"
                      />
                      <span
                        className="w-full truncate"
                        title={attachment.filename}
                      >
                        {attachment.filename}
                      </span>
                    </span>
                  )}
                </a>
                {attachment.isUploading && (
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-center text-[10px] font-medium text-white">
                    Uploading
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(attachment.id)}
                  className="absolute right-1 top-1 hidden rounded-full bg-black/70 p-0.5 text-white hover:bg-black group-hover/image:block focus:block"
                  aria-label={`Remove ${attachment.filename}`}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Collapsed, this row folds away instead of unmounting: the file input
            lives in here and must survive an open file dialog. `aria-hidden`
            takes it out of the accessibility tree; tabbing to a button in it
            focuses the form, which expands it again. */}
        <div
          aria-hidden={isCollapsed}
          className={classNames(
            "flex justify-between items-center px-4 overflow-hidden",
            "transition-[max-height,opacity,padding] duration-200 ease-out",
            isCollapsed
              ? "max-h-0 pb-0 opacity-0 pointer-events-none"
              : "max-h-16 pb-4 opacity-100",
          )}
        >
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={attachmentAccept}
              aria-label="Attach files"
              multiple
              className="hidden"
              onChange={handleChangeFileInput}
            />
            {(() => {
              const skillOption = (skill: SkillItem): DropdownOptionType => ({
                label: `/${skill.slug}`,
                labelClassName: "font-mono text-xs text-blue-link",
                className: "border-b border-gray-100",
                description: skill.description || undefined,
                onClick: () => insertSkillChip(skill),
              });
              const sectionHeaderClassName =
                "bg-gray-50 border-b border-gray-100";
              const personalSkills = skillItems.filter(
                (skill) => skill.visibility === "PERSONAL",
              );
              const orgSkills = skillItems.filter(
                (skill) =>
                  skill.visibility === "WORKSPACE" ||
                  skill.visibility === "PLATFORM",
              );
              const addOptions: DropdownOptionType[] = [
                ...(personalSkills.length > 0
                  ? [
                      {
                        type: "header",
                        label: "Your skills",
                        className: sectionHeaderClassName,
                      } as const,
                      ...personalSkills.map(skillOption),
                    ]
                  : []),
                ...(orgSkills.length > 0
                  ? [
                      {
                        type: "header",
                        label: currentOrganization?.name || "Organization",
                        className: sectionHeaderClassName,
                      } as const,
                      ...orgSkills.map(skillOption),
                    ]
                  : []),
                {
                  label: "Manage Skills",
                  onClick: () => {
                    if (
                      hasSubmittableContent &&
                      !window.confirm(
                        "You have unsaved content. Are you sure you want to leave?",
                      )
                    ) {
                      return;
                    }
                    history.push("/agent/skills");
                  },
                },
              ];
              return (
                <Dropdown
                  options={addOptions}
                  menuClassName="max-w-xs py-0! overflow-hidden"
                  aria-label="Add to message"
                >
                  <Button
                    icon={getTopicflowIcon("plus")}
                    theme={buttonTheme.iconGray}
                    size={ButtonSize.medium}
                    tooltip="Add to message"
                    aria-label="Add to message"
                  />
                </Dropdown>
              );
            })()}

            {(meetingId || assessmentId || showChatHistory) && (
              <ChatHistoryPopover onClickChatSessionId={onClickChatSessionId}>
                {cachedChatSession ? (
                  <AppPopover.Button
                    as={Button}
                    theme={buttonTheme.iconGray}
                    size={ButtonSize.medium}
                  >
                    <TopicflowIcon type="previous" className="h-4 w-4" />
                    {chatSessionTitle}
                  </AppPopover.Button>
                ) : undefined}
              </ChatHistoryPopover>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Upload files"
              title="Upload PDF, DOCX, TXT, Markdown, CSV (up to 4 MB) or images (up to 5 MB)"
              onClick={handleClickAddFile}
              disabled={
                attachments.length >= maxAskAIAttachments ||
                loadingCreateMessage ||
                loadingRetryAskAiMessage
              }
              className="h-6 w-6 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperClipIcon className="h-4 w-4" />
            </button>
            {showStopButton ? (
              <button
                type="button"
                aria-label="Stop generating"
                title="Stop generating"
                onClick={onStopResponse}
                className="h-6 w-6 flex items-center justify-center rounded-full bg-blue-link text-white hover:bg-blue-800"
              >
                <span className="h-2.5 w-2.5 bg-white" />
              </button>
            ) : (
              <button
                type="submit"
                aria-label="Send message"
                disabled={
                  !hasSubmittableContent ||
                  isUploadingFiles ||
                  loadingCreateMessage ||
                  loadingRetryAskAiMessage
                }
                className={buttonClassName}
              >
                <ArrowUpIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </form>
    );
  },
);

export default AskAIForm;
